import { useCallback, useMemo, useState } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { SimpleEditor } from "@/components/_organisms/tiptap/tiptap-templates/simple/simple-editor";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import type { JSONContent } from "@tiptap/react";

interface RichTextFieldCellProps {
  value: string;
  rawDescription?: string | null;
  labelKey: string;
  disabled?: boolean;
  onSave: (serialized: string) => void;
}

function parseJsonOrNull(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function hasMeaningfulRichTextContent(content: JSONContent): boolean {
  if (typeof content.text === "string" && content.text.trim() !== "") {
    return true;
  }
  if (content.type && content.type !== "doc" && content.type !== "paragraph") {
    return true;
  }
  return content.content?.some(hasMeaningfulRichTextContent) ?? false;
}

export function RichTextFieldCell({ value, rawDescription, labelKey, disabled, onSave }: RichTextFieldCellProps) {
  const { t } = useDomainTranslation("data-request");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ text: string; json: JSONContent } | null>(null);

  const initialEditorContent = useMemo(() => {
    const source = value.trim() !== "" ? value : rawDescription;
    if (!source) {
      return undefined;
    }
    const parsed = parseJsonOrNull(source);
    if (parsed && typeof parsed === "object" && "json" in parsed) {
      return { type: "json" as const, content: (parsed as { json: JSONContent }).json };
    }
    if (parsed && typeof parsed === "object" && "type" in parsed && (parsed as { type?: unknown }).type === "doc") {
      return { type: "json" as const, content: parsed as JSONContent };
    }
    return {
      type: "json" as const,
      content: {
        type: "doc",
        content: [{ type: "paragraph", content: [{ type: "text", text: source }] }],
      } as JSONContent,
    };
  }, [value, rawDescription]);

  const displayText = useMemo(() => {
    if (!value) {
      return "";
    }
    const parsed = parseJsonOrNull(value);
    if (parsed && typeof parsed === "object" && "text" in parsed) {
      const text = (parsed as { text?: unknown }).text;
      return typeof text === "string" ? text : "";
    }
    return value;
  }, [value]);

  const handleOpen = useCallback(() => {
    setDraft(null);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDraft(null);
    setOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    if (draft) {
      onSave(hasMeaningfulRichTextContent(draft.json) ? JSON.stringify({ text: draft.text, json: draft.json }) : "");
    }
    setDraft(null);
    setOpen(false);
  }, [draft, onSave]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-full justify-between text-sm font-normal"
        disabled={disabled}
        onClick={handleOpen}
        aria-label={t("dataEntry.fieldTable.editRichTextFor", { field: t(labelKey) })}
      >
        <span className="truncate text-left">{displayText}</span>
        <Pencil className="ml-1 h-3 w-3 shrink-0 opacity-50" />
      </Button>
      {open && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) {
              handleClose();
            }
          }}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t(labelKey)}</DialogTitle>
            </DialogHeader>
            <div className="max-h-96 min-h-64 overflow-y-auto rounded-md border">
              <SimpleEditor
                isInModal
                initialContent={initialEditorContent}
                placeholder=""
                onChange={(plainText, json) => setDraft({ text: plainText, json })}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t("dataEntry.cancel")}
              </Button>
              <Button onClick={handleSave}>{t("dataEntry.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
